import { useMemo } from "react";
import { DayPicker } from "react-day-picker";
import { AlertTriangle, CalendarRange } from "lucide-react";
import { sydneyToday, workDatesLabel } from "@/lib/dates";
import "react-day-picker/dist/style.css";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parse = (s) => new Date(`${s}T00:00:00`);

// Tap-to-pick hire days (any days, gaps allowed) or a flexible window; builder sees the red per-day payout note on 2+ days.
export default function WorkDatesPicker({ dates = [], flexible = false, windowEnd = "", durationDays = 1, onChange, single = false, testid = "work-dates" }) {
  const today = sydneyToday();
  const max = useMemo(() => { const d = parse(today); d.setMonth(d.getMonth() + 3); return d; }, [today]);
  const selected = flexible
    ? { from: dates[0] ? parse(dates[0]) : undefined, to: windowEnd ? parse(windowEnd) : undefined }
    : dates.map(parse);
  const set = (next) => onChange({ dates: next.dates ?? dates, flexible: next.flexible ?? flexible, windowEnd: next.windowEnd ?? windowEnd });
  const onSelect = (sel) => {
    if (flexible) return set({ dates: sel?.from ? [iso(sel.from)] : [], windowEnd: sel?.to ? iso(sel.to) : "" });
    const list = (sel || []).map(iso).sort();
    set({ dates: single ? list.slice(-1) : list });
  };
  const label = workDatesLabel({ work_dates: dates, flexible, start_date: dates[0], end_date: windowEnd, duration_days: durationDays });
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <label className="form-label !mb-0">{single ? "Which day?" : flexible ? (dates[0] && !windowEnd ? "Now tap the LAST possible day" : "Tap the first possible day, then the last") : "Tap the days you need them"}</label>
        {!single && (
          <button type="button" onClick={() => set({ flexible: !flexible, dates: flexible ? dates.slice(0, 1) : [], windowEnd: "" })} data-testid={`${testid}-flexible`}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-bold border ${flexible ? "border-[#FF6A00] bg-[#FF6A00] text-black" : "border-white/15 text-zinc-300 hover:border-[#FF6A00]"}`}>
            <CalendarRange className="w-3.5 h-3.5" /> {flexible ? "Flexible: on" : "Flexible dates"}
          </button>
        )}
      </div>
      <div className="border border-white/15 bg-black p-2 tl-daypicker max-h-[560px] overflow-y-auto" data-testid={`${testid}-scroll`}>
        <DayPicker mode={flexible ? "range" : "multiple"} selected={selected} onSelect={onSelect} fromDate={parse(today)} toDate={max} defaultMonth={parse(today)} weekStartsOn={1} showOutsideDays={false} numberOfMonths={4} disableNavigation />
      </div>
      <div className="mt-1 text-[10px] uppercase tracking-widest text-zinc-500">Scroll down for later months · up to 3 months ahead</div>
      <div className="mt-2 text-sm text-white font-semibold" data-testid={`${testid}-summary`}>{dates.length ? label : <span className="text-zinc-500 font-normal">No days picked yet</span>}</div>
      {flexible && <div className="text-xs text-zinc-400 mt-1" data-testid={`${testid}-flex-hint`}>{windowEnd ? "Window set — now say how many days inside it below. The tradie proposes days that suit." : "Start and end dates mark the window the job can happen in."}</div>}
      {!flexible && dates.length > 1 && (
        <div className="mt-2 flex items-start gap-2 text-xs text-red-400 font-semibold" data-testid={`${testid}-multiday-note`}>
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> Multi‑day booking: you'll tick off each day at knock‑off so the tradie is paid day by day.
        </div>
      )}
    </div>
  );
}
